import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { AlertBanner, Button, FileDropzone } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Download } from 'lucide-react';

import { useImportWizard } from '../../../../features/members-import/state';
import { getImportTemplate } from '../../../../services/importService.js';
import { parseSpreadsheet } from '../../../../services/spreadsheetParser.js';
import {
  ACCEPT_ATTRIBUTE,
  buildSampleFile,
  IMPORT_ERROR,
  MAX_BYTES,
  MAX_ROWS,
  SAMPLE_FILES,
} from '../../constants';
import { downloadTextFile } from '../../utils/downloadTextFile.js';

const MAX_MB = MAX_BYTES / (1024 * 1024);
const DROPZONE_ERROR = { type: IMPORT_ERROR.UNSUPPORTED_TYPE, size: IMPORT_ERROR.TOO_LARGE, multiple: IMPORT_ERROR.MULTIPLE };

/**
 * Step 1: drop zone or loaded-file card, sample files, template download and the error banner.
 * Parsing happens on the client; a rejected file never replaces the one already loaded.
 */
function ImportUploadStep() {
  const { t } = useTranslation('people-access');
  const { state, loadFile, parsing, setParsing } = useImportWizard();
  const [errorCode, setErrorCode] = useState(null);
  const [templateFailed, setTemplateFailed] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleFile = useCallback(
    async (file) => {
      setErrorCode(null);
      setParsing(true);
      try {
        const { headerRow, headers, rows, rowCount } = await parseSpreadsheet(file, {
          columnLabel: (position) => t('import.upload.columnFallback', { position }),
        });
        if (!mountedRef.current) return;
        loadFile({
          file: { name: file.name, size: file.size, headerRow, headers, rowCount },
          rawRows: rows,
        });
      } catch (error) {
        if (mountedRef.current) setErrorCode(error?.code ?? IMPORT_ERROR.UNREADABLE);
      } finally {
        // Released even if the step unmounted meanwhile: the flag belongs to the wizard.
        setParsing(false);
      }
    },
    [loadFile, setParsing, t],
  );

  const handleDropzoneError = useCallback(
    ({ code }) => setErrorCode(DROPZONE_ERROR[code] ?? IMPORT_ERROR.UNREADABLE),
    [],
  );
  const handleSample = useCallback(() => handleFile(buildSampleFile(SAMPLE_FILES.ROSTER)), [handleFile]);
  const handleManyColumns = useCallback(
    () => handleFile(buildSampleFile(SAMPLE_FILES.MANY_COLUMNS)),
    [handleFile],
  );
  const dismissError = useCallback(() => setErrorCode(null), []);

  const dismissTemplateError = useCallback(() => setTemplateFailed(false), []);

  const handleTemplate = useCallback(async () => {
    setTemplateFailed(false);
    try {
      const { body } = await getImportTemplate();
      downloadTextFile(body.file_name, body.content);
    } catch {
      if (mountedRef.current) setTemplateFailed(true);
    }
  }, []);

  const { file } = state;
  const card = file
    ? {
        name: file.name,
        summary: [
          t('import.upload.summary.people', { count: file.rowCount }),
          t('import.upload.summary.columns', { count: file.headers.length }),
          t('import.upload.summary.headerRow', { row: file.headerRow }),
        ].join(' · '),
      }
    : undefined;

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      {errorCode ? (
        <AlertBanner
          tone="danger"
          title={t(`import.errors.${errorCode}.title`)}
          dismissLabel={t('import.errors.dismiss')}
          onDismiss={dismissError}
        >
          {t(`import.errors.${errorCode}.message`, { size: MAX_MB, rows: MAX_ROWS })}
        </AlertBanner>
      ) : null}
      {templateFailed ? (
        <AlertBanner
          tone="danger"
          title={t('import.upload.templateFailed')}
          dismissLabel={t('import.errors.dismiss')}
          onDismiss={dismissTemplateError}
        />
      ) : null}
      <FileDropzone
        accept={ACCEPT_ATTRIBUTE}
        maxSizeBytes={MAX_BYTES}
        title={t('import.upload.dropTitle')}
        description={t('import.upload.dropDescription', { rows: MAX_ROWS, size: MAX_MB })}
        chooseLabel={t('import.upload.choose')}
        replaceLabel={t('import.upload.replace')}
        file={card}
        disabled={parsing}
        onFileSelected={handleFile}
        onError={handleDropzoneError}
      />
      {parsing ? (
        <p role="status" className="text-sm text-content-secondary">
          {t('import.upload.parsing')}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Button variant="secondary" size="sm" leftIcon={Download} onClick={handleTemplate}>
          {t('import.upload.downloadTemplate')}
        </Button>
        <span className="text-xs text-content-secondary">{t('import.upload.templateHint')}</span>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" disabled={parsing} onClick={handleSample}>
            {t('import.upload.trySample')}
          </Button>
          <Button variant="ghost" size="sm" disabled={parsing} onClick={handleManyColumns}>
            {t('import.upload.tryManyColumns')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default memo(ImportUploadStep);
